import React, {useState} from 'react'
import {useUpdatePasswordMutation} from '../../services/cognitoApi'
import {Button, Input} from 'react-daisyui'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faEye, faEyeSlash} from '@fortawesome/free-solid-svg-icons'

export const ResetPassword = ({onSuccess}: {onSuccess?: Function}) => {
  const [
    updatePasswordMutation,
    updatePasswordMutationResult
  ] = useUpdatePasswordMutation()

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setErrorMessage(null)
    const target = e.target as HTMLFormElement
    if (
      (target.elements as any).newPassword.value !==
      (target.elements as any).confirmPassword.value
    ) {
      setErrorMessage('Passwords do not match!')
      return
    }
    updatePasswordMutation({
      oldPassword: (target.elements as any).oldPassword.value,
      newPassword: (target.elements as any).newPassword.value
    })
      .unwrap()
      .then(() => {
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch(error => {
        return
      })
  }

  return (
    <div className="grid gap-2">
      <h1 className="font-semibold text-xl">Reset Password</h1>
      {errorMessage ? (
        <div
          className="text-red-500"
          role="alert"
          aria-label={`Error in reset form ${errorMessage}`}
          aria-live="assertive"
        >
          {errorMessage}
        </div>
      ) : (
        ''
      )}
      {updatePasswordMutationResult.isUninitialized ? (
        <form className="form-control " onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-2">
            <div>
              <label
                className="label pb-1 text-sm font-light"
                htmlFor="oldPassword"
              >
                Current Password
              </label>
              <div className="relative">
                <Input
                  id="oldPassword"
                  name="oldPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your current password"
                  className="border-1 border-cabana bg-white w-full"
                  aria-required="true"
                  aria-label="Current Password"
                ></Input>
                <span
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  role="button"
                >
                  {showPassword ? (
                    <FontAwesomeIcon className="text-gray-400" icon={faEye} />
                  ) : (
                    <FontAwesomeIcon
                      className="text-gray-400"
                      icon={faEyeSlash}
                    />
                  )}
                </span>
              </div>
            </div>

            <div>
              <label
                className="label pb-1 text-sm font-light"
                htmlFor="newPassword"
              >
                New Password
              </label>
              <div className="relative">
                <Input
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your new password"
                  className="border-1 border-cabana bg-white w-full"
                  aria-required="true"
                  aria-label="New Password"
                  onChange={e => {
                    const confirmPasswordInput = (e.target.form as any)
                      .confirmPassword
                    if (
                      confirmPasswordInput.value &&
                      confirmPasswordInput.value !== e.target.value
                    ) {
                      setErrorMessage('Passwords do not match!')
                    } else {
                      setErrorMessage(null)
                    }
                  }}
                ></Input>
                <span
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  role="button"
                >
                  {showPassword ? (
                    <FontAwesomeIcon className="text-gray-400" icon={faEye} />
                  ) : (
                    <FontAwesomeIcon
                      className="text-gray-400"
                      icon={faEyeSlash}
                    />
                  )}
                </span>
              </div>
            </div>
            <div>
              <label
                className="label pb-1 text-sm font-light"
                htmlFor="confirmPassword"
              >
                Confirm Password
              </label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Confirm your new password"
                className="border-1 border-cabana bg-white w-full"
                aria-required="true"
                aria-label="Confirm Password"
                onChange={e => {
                  const newPasswordInput = (e.target.form as any).newPassword
                  if (
                    e.target.value &&
                    e.target.value !== newPasswordInput.value
                  ) {
                    setErrorMessage('Passwords do not match!')
                  } else {
                    setErrorMessage(null)
                  }
                }}
              ></Input>
            </div>
            <Button
              className="mt-6 btn-md text-lg font-semibold lowercase w-full"
              color="secondary"
              type="submit"
              aria-label="Reset Password"
            >
              Reset Password
            </Button>
          </div>
        </form>
      ) : (
        ''
      )}

      {updatePasswordMutationResult.error && (
        <div>
          <div className="text-red-500" role="alert">
            {updatePasswordMutationResult.error.toString()}
          </div>
          <Button
            className="btn btn-link no-underline text-sm w-full"
            onClick={() => {
              updatePasswordMutationResult.reset()
              setErrorMessage(null)
            }}
            aria-label="Try resetting password again"
          >
            try again
          </Button>
        </div>
      )}
      {updatePasswordMutationResult.isLoading && (
        <div className="pt-4" role="status" aria-live="polite">
          <LoadingSpinner size={LoadingSpinnerSize.Large}></LoadingSpinner>
        </div>
      )}
      {updatePasswordMutationResult.isSuccess && (
        <div role="status" aria-live="polite">
          Password Updated
        </div>
      )}
    </div>
  )
}

export default ResetPassword
